import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { onePagersReduced as onePagerMetaList, OnePagerMeta } from "@/data/one-pagers";
import { cn } from "@/lib/utils";
import { Helmet } from "react-helmet-async";

const PDF_MARKDOWN_CLASSNAME =
  "prose prose-slate max-w-none text-[14px] leading-6 prose-headings:font-semibold prose-headings:text-neutral-900 prose-strong:text-neutral-900 prose-a:text-neutral-900 prose-a:underline prose-li:marker:text-neutral-400";

const PdfExport = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  const activeMeta = useMemo(() => onePagerMetaList.find(item => item.slug === slug), [slug]);

  useEffect(() => {
    if (!activeMeta) return;
    const controller = new AbortController();

    async function loadMarkdown() {
      try {
        setIsLoading(true);
        setHasError(false);
        const response = await fetch(`/one-pagers/${activeMeta.filename}`, { signal: controller.signal });
        if (!response.ok) throw new Error(`Failed to load ${activeMeta.filename}`);
        const text = await response.text();
        setContent(text);
      } catch {
        if (!controller.signal.aborted) setHasError(true);
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }

    loadMarkdown();

    return () => controller.abort();
  }, [activeMeta]);

  useEffect(() => {
    if (!activeMeta || isLoading || hasError) return;
    const timeout = window.setTimeout(() => {
      window.print();
    }, 600);
    return () => window.clearTimeout(timeout);
  }, [activeMeta, isLoading, hasError]);

  if (!activeMeta) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6 text-center text-neutral-900">
        <p className="text-lg font-semibold">We couldn&apos;t find that one-pager.</p>
        <Button className="mt-6" onClick={() => navigate("/one-pagers")}>
          Back to library
        </Button>
      </div>
    );
  }

  return (
    <div id="pdf-root" className="min-h-screen bg-white text-neutral-900 print:min-h-0">
      <Helmet>
        <style>{`
          @page {
            size: A4 portrait;
            margin: 0;
          }
          @media print {
            body {
              background: #ffffff;
            }
            #pdf-root {
              background: #ffffff;
            }
          }
        `}</style>
      </Helmet>
      <div className="mx-auto w-full max-w-[595px] print:mx-0 print:max-w-[595px] print:px-0 print:pb-[80px]">
        <div className="bg-neutral-100 px-6 py-3 text-xs text-neutral-600 print:hidden">
          <div className="flex items-center justify-between gap-4">
            <p className="max-w-[65%] leading-5">This is a preview of the exported PDF. Use the button to trigger print or “Save as PDF”.</p>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => window.print()}>
                Print / Save as PDF
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate(-1)}>
                Close
              </Button>
            </div>
          </div>
        </div>

        <div
          className="relative h-[185px] w-full bg-cover bg-no-repeat"
          style={{ backgroundImage: "url('/assets/letterhead-cover.png')" }}
        >
          <img
            src="/assets/logo-light-mode.png"
            alt="OneWeekMVPs"
            className="absolute left-10 top-[120px] h-10 w-auto"
          />
        </div>

        <div className="px-8 pt-8 pb-4 text-xs font-medium uppercase tracking-[0.35em] text-neutral-500 print:px-10">
          <p>oneweekmvps.com</p>
        </div>

        <main className="px-8 pb-10 print:px-10">
          <header>
            <h1 className="text-[24px] font-semibold text-neutral-900 leading-tight">{activeMeta.title}</h1>
            <p className="mt-2 text-[15px] leading-6 text-neutral-600">{activeMeta.description}</p>
          </header>

          <section className="mt-6 flex flex-wrap items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-neutral-500">
            <span>Build budget · {activeMeta.costRange.label}</span>
            <span>Recurring · {activeMeta.monthlyCostRange.label}</span>
            <span>{activeMeta.durationWeeks}-week delivery</span>
          </section>

          <section className="mt-6 flex flex-wrap gap-1.5">
            {activeMeta.tags.map(tag => (
              <span
                key={tag}
                className="rounded-full border border-neutral-200 px-3 py-1 text-[11px] font-medium text-neutral-600"
              >
                {tag}
              </span>
            ))}
          </section>

          <section className="mt-8 grid gap-4 text-[13px] text-neutral-600">
            <MetaList title="Industries" items={activeMeta.industries} />
            <MetaList title="Tech stack" items={activeMeta.tools} />
          </section>

          <div className="mt-8 border-t border-neutral-200 pt-6 text-neutral-800">
            {isLoading ? (
              <Skeleton className="h-8 w-full rounded-lg" />
            ) : hasError ? (
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-6 text-sm text-neutral-600">
                We couldn&apos;t load the one-pager content. Please try again from the main library.
              </div>
            ) : (
              <ReactMarkdown
                className={PDF_MARKDOWN_CLASSNAME}
                remarkPlugins={[remarkGfm]}
                components={{
                  ...markdownComponents,
                  h1: ({ className, ...props }) => (
                    <h2 className={cn("mt-8 text-xl font-semibold text-neutral-900", className)} {...props} />
                  ),
                  h2: ({ className, ...props }) => (
                    <h3 className={cn("mt-7 text-lg font-semibold text-neutral-900", className)} {...props} />
                  ),
                  h3: ({ className, ...props }) => (
                    <h4 className={cn("mt-6 text-sm font-semibold uppercase tracking-wide text-neutral-700", className)} {...props} />
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

const markdownComponents = {
  p: ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p className={cn("mt-3 leading-6 text-neutral-700", className)} {...props} />
  ),
  ul: ({ className, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
    <ul className={cn("mt-3 list-disc space-y-1.5 pl-5 text-neutral-700", className)} {...props} />
  ),
  ol: ({ className, ...props }: React.HTMLAttributes<HTMLOListElement>) => (
    <ol className={cn("mt-3 list-decimal space-y-1.5 pl-5 text-neutral-700", className)} {...props} />
  ),
  li: ({ className, ...props }: React.HTMLAttributes<HTMLLIElement>) => <li className={cn("pl-1", className)} {...props} />,
  strong: ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
    <strong className={cn("font-semibold text-neutral-900", className)} {...props} />
  ),
  a: ({ className, ...props }: React.HTMLAttributes<HTMLAnchorElement>) => (
    <a className={cn("font-medium text-neutral-900 underline underline-offset-2", className)} {...props} />
  ),
};

const MetaList = ({ title, items }: { title: string; items: string[] }) => (
  <div>
    <h3 className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-500">{title}</h3>
    <div className="mt-2 flex flex-wrap gap-1.5">
      {items.map(item => (
        <span key={item} className="rounded-full border border-neutral-200 px-2.5 py-1 text-[11px] text-neutral-600">
          {item}
        </span>
      ))}
    </div>
  </div>
);

const CoverGraphic = () => (
  <svg width="595" height="185" viewBox="0 0 595 185" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
    <g clipPath="url(#clip0_108_843)">
      <path
        d="M223.5 7.49991H-1V5H595V180.032C595.107 183.293 595.108 186.615 595 190V180.032C589.96 25.7069 348.852 9.45862 223.5 7.49991Z"
        fill="black"
        stroke="black"
      />
      <rect x="-1" y="-5" width="596" height="13" fill="black" />
      <g opacity="0.25">
        <path
          d="M564.42 39.9764H566.38C566.784 39.9764 567.133 39.6945 567.219 39.2996L569.775 27.4949C569.891 26.9599 569.483 26.4546 568.936 26.4546H564.439C564.102 26.4546 563.796 26.6517 563.657 26.9585L561.553 31.5954C561.182 32.4147 559.96 32.1833 559.914 31.2849L559.707 27.2689C559.684 26.8126 559.307 26.4546 558.85 26.4546H556.339C555.961 26.4546 555.628 26.7011 555.518 27.0621L551.909 38.8668C551.74 39.4186 552.153 39.9764 552.73 39.9764H554.061C554.466 39.9764 554.816 39.6928 554.9 39.2962L555.423 36.8344C555.633 35.8483 557.076 35.9677 557.121 36.9748L557.217 39.1558C557.238 39.6147 557.616 39.9764 558.075 39.9764H560.239C560.572 39.9764 560.875 39.7839 561.017 39.4824L562.297 36.7516C562.729 35.8317 564.119 36.2917 563.916 37.2874L563.578 38.9466C563.47 39.4787 563.877 39.9764 564.42 39.9764Z"
          fill="white"
        />
        <path
          d="M538.586 22H540.859C541.333 22 541.717 22.3844 541.717 22.8585V30.5771C541.717 31.4368 542.841 31.7629 543.301 31.0367L545.971 26.8243C546.128 26.5759 546.402 26.4253 546.696 26.4253H549.116C549.59 26.4253 549.974 26.8097 549.974 27.2839V30.6889C549.974 31.599 551.204 31.8836 551.604 31.0661L553.638 26.9067C553.782 26.6121 554.081 26.4253 554.409 26.4253H557.297C558.028 26.4253 558.424 27.2808 557.952 27.8387L556.373 29.7032C556.316 29.7705 556.27 29.8463 556.236 29.9277L552.259 39.4699C552.125 39.7898 551.813 39.9981 551.466 39.9981H551.077C550.641 39.9981 550.279 39.6696 550.201 39.2403C549.804 37.0543 548.839 36.1685 546.43 35.4321C546.308 35.3949 546.224 35.2831 546.224 35.156V33.4298C546.224 33.139 545.84 33.0336 545.691 33.2835L544.451 35.3686C544.42 35.4223 544.37 35.4644 544.312 35.4882C542.311 36.3155 541.523 37.2191 541.124 39.2602C541.042 39.6807 540.683 39.9981 540.255 39.9981H537.786C537.312 39.9981 536.928 39.6138 536.928 39.1396V28.2283C536.928 27.7541 536.543 27.3698 536.069 27.3698H534.859C534.384 27.3698 534 26.9854 534 26.5112V26.1138C534 25.866 534.107 25.6302 534.294 25.4672L538.021 22.2119C538.177 22.0753 538.378 22 538.586 22Z"
          fill="white"
        />
        <circle cx="545.657" cy="39.9445" r="3.72375" fill="#BCF23D" />
      </g>
    </g>
    <defs>
      <clipPath id="clip0_108_843">
        <rect width="595" height="185" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export default PdfExport;

